import React from "react";
import { motion } from "framer-motion";
import { Compass, PenTool, Code2, Rocket } from "lucide-react";
import { PROCESS } from "@/constants/testIds";

const STEPS = [
  { n: "01", icon: Compass, title: "Discover", body: "We start with the problem, not the tech. Workshops, interviews, audits — until the brief is crystal clear." },
  { n: "02", icon: PenTool, title: "Design", body: "Wireframes, motion prototypes, design systems. Everything signed off before a single line of production code." },
  { n: "03", icon: Code2, title: "Engineer", body: "Senior engineers, embedded squads, weekly demos. We write tests, ship in branches, and review every PR." },
  { n: "04", icon: Rocket, title: "Launch & scale", body: "Observability, on-call, performance reviews. We stay around for what comes after launch — not just the demo day." },
];

export default function Process() {
  return (
    <section
      id="process"
      data-testid={PROCESS.section}
      className="relative py-20 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10">
        <div className="mb-12 md:mb-24">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-[#0A84FF] mb-3 md:mb-4">
            How we work
          </p>
          <h2 className="font-display text-[10vw] sm:text-5xl lg:text-7xl font-black tracking-[-0.03em] leading-[1.02] text-grad max-w-3xl">
            A calm, predictable process.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                data-testid={PROCESS.step(s.n)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="glass rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl bg-[#0A84FF]/20" />
                <div className="relative flex items-start justify-between mb-6 md:mb-8">
                  <span className="font-display text-4xl sm:text-5xl md:text-6xl font-black text-white/10 tracking-tight">
                    {s.n}
                  </span>
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl md:rounded-2xl bg-white/5 border border-white/10 grid place-items-center">
                    <Icon size={18} strokeWidth={1.8} className="text-white" />
                  </div>
                </div>
                <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight mb-2 md:mb-3">
                  {s.title}
                </h3>
                <p className="text-[#a1a1a6] text-sm md:text-base leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
